import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Empty } from 'antd';
import GaugeComponent from 'react-gauge-component';
import './GaugeWidget.scss';

const GaugeWidget = ({ data, config }) => {
  const containerRef = useRef(null);
  const [key, setKey] = useState(0);

  // Si no hay datos, usar valores mock para visualización inicial
  const widgetData = data && data.valorActual !== undefined ? data : {
    valorActual: 50,
    valorMinimo: 0,
    valorMaximo: 100,
    porcentaje: 50,
    unidades: '',
    estado: 'aceptable',
  };
  
  // Detectar cambios de tamaño y forzar re-render
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      // Forzar re-render del gauge cambiando la key
      setKey(prevKey => prevKey + 1);
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  if (!widgetData) {
    return <Empty description="No hay datos disponibles" />;
  }

  // Construir arcos del gauge basado en umbrales
  // El backend ya calcula los umbrales y los envía en data.umbrales
  const getArcColors = () => {
    // Si el backend envía umbrales calculados, usar sus colores
    if (widgetData.umbrales) {
      return [
        widgetData.umbrales.danger?.color || '#f44336',   // Rojo izquierdo
        widgetData.umbrales.warning?.color || '#ff9800',  // Amarillo izquierdo
        widgetData.umbrales.aceptable?.color || '#4caf50', // Verde
        widgetData.umbrales.warning?.color || '#ff9800',  // Amarillo derecho
        widgetData.umbrales.danger?.color || '#f44336'    // Rojo derecho
      ];
    }
    return ['#f44336', '#ff9800', '#4caf50', '#ff9800', '#f44336'];
  };

  // Función para validar y ajustar límites dentro del rango [min, max]
  const validateLimits = (limits, min, max) => {
    // Asegurar que todos los límites estén dentro del rango y sean ascendentes
    const validatedLimits = [];
    let previousLimit = min;

    for (let i = 0; i < limits.length; i++) {
      let currentLimit = limits[i];
      
      // Forzar dentro del rango [min, max]
      currentLimit = Math.max(min, Math.min(max, currentLimit));
      
      // Asegurar que sea mayor que el límite anterior
      if (currentLimit <= previousLimit) {
        const increment = (max - min) * 0.01; // 1% del rango
        currentLimit = Math.min(previousLimit + increment, max);
      }
      
      validatedLimits.push(currentLimit);
      previousLimit = currentLimit;
    }

    // Verificar que el último límite sea exactamente max
    validatedLimits[validatedLimits.length - 1] = max;

    return validatedLimits;
  };

  // Calcular límites de arcos usando los umbrales del backend
  const getArcLimits = () => {
    try {
      // Si el backend envía umbrales calculados, verificar que tengan valores válidos
      if (widgetData.umbrales) {
        const { aceptable, warning, danger } = widgetData.umbrales;
        
        // Verificar si los umbrales tienen valores válidos (no null/undefined)
        const aceptableValido = aceptable && aceptable.min !== null && aceptable.max !== null;
        const warningValido = warning && warning.min !== null && warning.max !== null;
        const dangerValido = danger && danger.min !== null && danger.max !== null;

        // Si todos los umbrales son válidos, usar la lógica completa
        if (aceptableValido && warningValido && dangerValido) {
          const rangoVerde = aceptable.max - aceptable.min;
          const margen = rangoVerde * 0.25;
          
          const gaugeMin = Math.max(danger.min, warning.min - margen);
          const gaugeMax = Math.min(danger.max, warning.max + margen);
          
          // Validar que el rango sea válido
          if (gaugeMin >= gaugeMax) {
            throw new Error('Rango inválido: min >= max');
          }
          
          const rangoGauge = gaugeMax - gaugeMin;
          const incremento = rangoGauge * 0.01;

          let limits = [
            Math.max(gaugeMin + incremento, Math.min(gaugeMax - incremento * 4, warning.min)),
            Math.max(gaugeMin + incremento * 2, Math.min(gaugeMax - incremento * 3, aceptable.min)),
            Math.max(gaugeMin + incremento * 3, Math.min(gaugeMax - incremento * 2, aceptable.max)),
            Math.max(gaugeMin + incremento * 4, Math.min(gaugeMax - incremento, warning.max)),
            gaugeMax
          ];

          // Validar y ajustar límites
          limits = validateLimits(limits, gaugeMin, gaugeMax);

          return {
            min: gaugeMin,
            max: gaugeMax,
            limits: limits,
            error: null
          };
        }

        // Si solo danger tiene valores (caso común para sensores sin rango "aceptable")
        if (dangerValido) {
          const gaugeMin = danger.min;
          const gaugeMax = danger.max;
          
          // Validar que el rango sea válido
          if (gaugeMin >= gaugeMax || !isFinite(gaugeMin) || !isFinite(gaugeMax)) {
            throw new Error(`Rango inválido: [${gaugeMin}, ${gaugeMax}]`);
          }
          
          const rango = gaugeMax - gaugeMin;
          
          let limits = [
            gaugeMin + rango * 0.2,   // Rojo izquierdo (20%)
            gaugeMin + rango * 0.35,  // Amarillo izquierdo (35%)
            gaugeMin + rango * 0.65,  // Verde (65%)
            gaugeMin + rango * 0.8,   // Amarillo derecho (80%)
            gaugeMax                  // Rojo derecho (100%)
          ];

          // Validar y ajustar límites
          limits = validateLimits(limits, gaugeMin, gaugeMax);
          
          return {
            min: gaugeMin,
            max: gaugeMax,
            limits: limits,
            error: null
          };
        }
      }

      // Si no hay umbrales válidos, usar valores por defecto
      const rangoMin = widgetData.valorMinimo ?? 0;
      const rangoMax = widgetData.valorMaximo ?? 100;
      
      // Validar que el rango sea válido
      if (rangoMin >= rangoMax || !isFinite(rangoMin) || !isFinite(rangoMax)) {
        throw new Error(`Rango por defecto inválido: [${rangoMin}, ${rangoMax}]`);
      }
      
      const rango = rangoMax - rangoMin;
      
      let limits = [
        rangoMin + rango * 0.2,   // Rojo izquierdo
        rangoMin + rango * 0.35,  // Amarillo izquierdo
        rangoMin + rango * 0.65,  // Verde
        rangoMin + rango * 0.8,   // Amarillo derecho
        rangoMax                  // Rojo derecho
      ];

      // Validar y ajustar límites
      limits = validateLimits(limits, rangoMin, rangoMax);
      
      return {
        min: rangoMin,
        max: rangoMax,
        limits: limits,
        error: null
      };
    } catch (error) {
      // Si hay cualquier error, devolver un error legible
      console.error('[GaugeWidget] Error calculando límites:', error);
      return {
        min: 0,
        max: 100,
        limits: [20, 35, 65, 80, 100],
        error: `Error en configuración: ${error.message}`
      };
    }
  };

  // Calcular valores para el gauge
  const arcData = getArcLimits();
  const minValue = arcData.min;
  const maxValue = arcData.max;
  const arcLimits = arcData.limits;
  const configError = arcData.error;
  
  const currentValue = widgetData.valorActual ?? 50;

  // Si hay un error de configuración, mostrar mensaje de error
  if (configError) {
    return (
      <div className="gauge-widget-error">
        <div className="error-icon">⚠️</div>
        <div className="error-title">Error de Configuración</div>
        <div className="error-message">{configError}</div>
        <div className="error-detail">
          Los límites del sensor están fuera del rango válido.
          <br />
          Por favor, verifica la configuración de umbrales del sensor.
        </div>
      </div>
    );
  }

  // Determinar el diseño del gauge (default: circular)
  const diseno = config.diseno || 'circular';

  // Renderizar según el diseño seleccionado
  const renderGaugeByDesign = () => {
    switch (diseno) {
      case 'horizontal':
        return renderBarraHorizontal();
      case 'vertical':
        return renderBarraVertical();
      case 'semicircular':
        return renderSemicircular();
      case 'circular':
      default:
        return renderCircular();
    }
  };

  // Renderizar gauge circular (360°)
  const renderCircular = () => (
    <>
      <div className="gauge-center" ref={containerRef}>
        <div className="gauge-container">
          <GaugeComponent
            key={key}
            type="radial"
            arc={{
              colorArray: getArcColors(),
              padding: 0.02,
              subArcs: [
                { limit: arcLimits[0] }, // Rojo izquierdo
                { limit: arcLimits[1] }, // Amarillo izquierdo
                { limit: arcLimits[2] }, // Verde
                { limit: arcLimits[3] }, // Amarillo derecho
                { limit: arcLimits[4] }, // Rojo derecho
              ],
            }}
            pointer={{
              elastic: true,
              animationDelay: 0,
            }}
            value={currentValue}
            minValue={minValue}
            maxValue={maxValue}
            labels={{
              valueLabel: {
                formatTextValue: (value) =>
                  `${value.toFixed(1)} ${widgetData.unidades || ''}`,
                style: {
                  fontSize: 'clamp(20px, 3.5vw, 28px)',
                  fill: '#262626',
                  fontWeight: 'bold',
                },
              },
              tickLabels: {
                type: 'inner',
                ticks: [
                  { value: minValue },
                  { value: (minValue + maxValue) / 2 },
                  { value: maxValue },
                ],
                defaultTickValueConfig: {
                  style: {
                    fontSize: 'clamp(9px, 1.3vw, 11px)',
                  },
                },
              },
            }}
          />
        </div>
      </div>

      {/* Información en la parte inferior */}
      <div className="gauge-bottom-info">
        {widgetData.estadisticas?.promedioHoy !== undefined && (
          <div className="info-badge">
            <span className="info-label">Prom. Hoy:</span>
            <span className="info-value">
              {widgetData.estadisticas.promedioHoy.toFixed(1)} {widgetData.unidades}
            </span>
          </div>
        )}

        {widgetData.estadisticas?.promedioMes !== undefined && (
          <div className="info-badge">
            <span className="info-label">Prom. Mes:</span>
            <span className="info-value">
              {widgetData.estadisticas.promedioMes.toFixed(1)} {widgetData.unidades}
            </span>
          </div>
        )}
      </div>
    </>
  );

  // Renderizar gauge semicircular (180°)
  const renderSemicircular = () => (
    <>
      <div className="gauge-center" ref={containerRef}>
        <div className="gauge-container">
          <GaugeComponent
            key={key}
            type="semicircle"
            arc={{
              colorArray: getArcColors(),
              padding: 0.02,
              subArcs: [
                { limit: arcLimits[0] },
                { limit: arcLimits[1] },
                { limit: arcLimits[2] },
                { limit: arcLimits[3] },
                { limit: arcLimits[4] },
              ],
            }}
            pointer={{
              elastic: true,
              animationDelay: 0,
            }}
            value={currentValue}
            minValue={minValue}
            maxValue={maxValue}
            labels={{
              valueLabel: {
                formatTextValue: (value) =>
                  `${value.toFixed(1)} ${widgetData.unidades || ''}`,
                style: {
                  fontSize: 'clamp(18px, 3vw, 24px)',
                  fill: '#262626',
                  fontWeight: 'bold',
                },
              },
              tickLabels: {
                type: 'inner',
                ticks: [
                  { value: minValue },
                  { value: (minValue + maxValue) / 2 },
                  { value: maxValue },
                ],
                defaultTickValueConfig: {
                  style: {
                    fontSize: 'clamp(8px, 1.2vw, 10px)',
                  },
                },
              },
            }}
          />
        </div>
      </div>

      <div className="gauge-bottom-info">
        {widgetData.estadisticas?.promedioHoy !== undefined && (
          <div className="info-badge">
            <span className="info-label">Prom. Hoy:</span>
            <span className="info-value">
              {widgetData.estadisticas.promedioHoy.toFixed(1)} {widgetData.unidades}
            </span>
          </div>
        )}

        {widgetData.estadisticas?.promedioMes !== undefined && (
          <div className="info-badge">
            <span className="info-label">Prom. Mes:</span>
            <span className="info-value">
              {widgetData.estadisticas.promedioMes.toFixed(1)} {widgetData.unidades}
            </span>
          </div>
        )}
      </div>
    </>
  );

  // Renderizar barra horizontal
  const renderBarraHorizontal = () => {
    const porcentaje = ((currentValue - minValue) / (maxValue - minValue)) * 100;
    const colors = getArcColors();
    
    // Calcular porcentajes para cada zona de color
    const getColorZones = () => {
      const total = maxValue - minValue;
      return [
        { width: ((arcLimits[0] - minValue) / total) * 100, color: colors[0] }, // Rojo izq
        { width: ((arcLimits[1] - arcLimits[0]) / total) * 100, color: colors[1] }, // Amarillo izq
        { width: ((arcLimits[2] - arcLimits[1]) / total) * 100, color: colors[2] }, // Verde
        { width: ((arcLimits[3] - arcLimits[2]) / total) * 100, color: colors[3] }, // Amarillo der
        { width: ((maxValue - arcLimits[3]) / total) * 100, color: colors[4] }, // Rojo der
      ];
    };

    const colorZones = getColorZones();

    return (
      <>
        <div className="gauge-bar-horizontal">
          <div className="gauge-bar-value">
            <span className="value-large">{currentValue.toFixed(1)}</span>
            <span className="value-unit">{widgetData.unidades}</span>
          </div>

          <div className="gauge-bar-container">
            {/* Segmentos de colores fijos */}
            <div className="gauge-bar-segments">
              {colorZones.map((zone, idx) => (
                <div
                  key={idx}
                  className="gauge-bar-segment"
                  style={{
                    width: `${zone.width}%`,
                    backgroundColor: zone.color,
                  }}
                />
              ))}
            </div>
            
            {/* Marcador del valor actual (línea roja) */}
            <div 
              className="gauge-bar-pointer"
              style={{
                left: `${Math.min(100, Math.max(0, porcentaje))}%`,
              }}
            >
              <div className="gauge-bar-pointer-line" />
              <div className="gauge-bar-pointer-arrow" />
            </div>
          </div>

          <div className="gauge-bar-labels">
            <span>{minValue.toFixed(0)}</span>
            <span>{maxValue.toFixed(0)}</span>
          </div>
        </div>

        <div className="gauge-bottom-info">
          {widgetData.estadisticas?.promedioHoy !== undefined && (
            <div className="info-badge">
              <span className="info-label">Prom. Hoy:</span>
              <span className="info-value">
                {widgetData.estadisticas.promedioHoy.toFixed(1)} {widgetData.unidades}
              </span>
            </div>
          )}

          {widgetData.estadisticas?.promedioMes !== undefined && (
            <div className="info-badge">
              <span className="info-label">Prom. Mes:</span>
              <span className="info-value">
                {widgetData.estadisticas.promedioMes.toFixed(1)} {widgetData.unidades}
              </span>
            </div>
          )}
        </div>
      </>
    );
  };

  // Renderizar barra vertical
  const renderBarraVertical = () => {
    const porcentaje = ((currentValue - minValue) / (maxValue - minValue)) * 100;
    const colors = getArcColors();
    
    // Calcular porcentajes para cada zona de color
    const getColorZones = () => {
      const total = maxValue - minValue;
      return [
        { height: ((arcLimits[0] - minValue) / total) * 100, color: colors[0] }, // Rojo inferior
        { height: ((arcLimits[1] - arcLimits[0]) / total) * 100, color: colors[1] }, // Amarillo inferior
        { height: ((arcLimits[2] - arcLimits[1]) / total) * 100, color: colors[2] }, // Verde
        { height: ((arcLimits[3] - arcLimits[2]) / total) * 100, color: colors[3] }, // Amarillo superior
        { height: ((maxValue - arcLimits[3]) / total) * 100, color: colors[4] }, // Rojo superior
      ];
    };

    const colorZones = getColorZones();

    return (
      <>
        <div className="gauge-bar-vertical">
          <div className="gauge-bar-value">
            <span className="value-large">{currentValue.toFixed(1)}</span>
            <span className="value-unit">{widgetData.unidades}</span>
          </div>

          <div className="gauge-bar-container-vertical">
            <div className="gauge-bar-labels-vertical">
              <span>{maxValue.toFixed(0)}</span>
              <span>{minValue.toFixed(0)}</span>
            </div>
            
            <div className="gauge-bar-track">
              {/* Segmentos de colores fijos (de abajo hacia arriba) */}
              <div className="gauge-bar-segments-vertical">
                {colorZones.map((zone, idx) => (
                  <div
                    key={idx}
                    className="gauge-bar-segment-vertical"
                    style={{
                      height: `${zone.height}%`,
                      backgroundColor: zone.color,
                    }}
                  />
                ))}
              </div>

              {/* Marcador del valor actual (línea roja horizontal) */}
              <div 
                className="gauge-bar-pointer-vertical"
                style={{
                  bottom: `${Math.min(100, Math.max(0, porcentaje))}%`,
                }}
              >
                <div className="gauge-bar-pointer-line-vertical" />
                <div className="gauge-bar-pointer-arrow-vertical">
                  <div className="pointer-circle" />
                  <div className="pointer-triangle" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="gauge-bottom-info">
          {widgetData.estadisticas?.promedioHoy !== undefined && (
            <div className="info-badge">
              <span className="info-label">Prom. Hoy:</span>
              <span className="info-value">
                {widgetData.estadisticas.promedioHoy.toFixed(1)} {widgetData.unidades}
              </span>
            </div>
          )}

          {widgetData.estadisticas?.promedioMes !== undefined && (
            <div className="info-badge">
              <span className="info-label">Prom. Mes:</span>
              <span className="info-value">
                {widgetData.estadisticas.promedioMes.toFixed(1)} {widgetData.unidades}
              </span>
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <div className="gauge-widget">
      {/* Timestamp discreto en esquina superior izquierda */}
      {widgetData.ultimaActualizacion && (
        <div className="gauge-timestamp-corner">
          {new Date(widgetData.ultimaActualizacion).toLocaleTimeString('es-MX', { 
            hour: '2-digit', 
            minute: '2-digit' 
          })}
        </div>
      )}

      {/* Renderizar diseño seleccionado */}
      {renderGaugeByDesign()}
    </div>
  );
};

GaugeWidget.propTypes = {
  data: PropTypes.shape({
    valorActual: PropTypes.number.isRequired,
    valorMinimo: PropTypes.number,
    valorMaximo: PropTypes.number,
    unidades: PropTypes.string,
    estado: PropTypes.oneOf(['danger', 'warning', 'aceptable']),
    // Umbrales calculados por el backend
    umbrales: PropTypes.shape({
      aceptable: PropTypes.shape({
        min: PropTypes.number,
        max: PropTypes.number,
        color: PropTypes.string,
        label: PropTypes.string,
      }),
      warning: PropTypes.shape({
        min: PropTypes.number,
        max: PropTypes.number,
        color: PropTypes.string,
        label: PropTypes.string,
      }),
      danger: PropTypes.shape({
        min: PropTypes.number,
        max: PropTypes.number,
        color: PropTypes.string,
        label: PropTypes.string,
      }),
    }),
    estadisticas: PropTypes.shape({
      promedioHoy: PropTypes.number,
      promedioMes: PropTypes.number,
    }),
    ultimaActualizacion: PropTypes.string,
  }).isRequired,
  config: PropTypes.shape({
    titulo: PropTypes.string,
    sensorId: PropTypes.string,
    autoRefresh: PropTypes.number,
    // Configuración de umbrales (formato simplificado para el backend)
    minAceptable: PropTypes.number,
    maxAceptable: PropTypes.number,
    tolerancia: PropTypes.number,
  }).isRequired,
};

export default GaugeWidget;
